<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  isIntegrationPricingPlan,
  isReadOnlyPricingPlan,
} from 'dashboard/composables/useShijiBookingLink';
import Button from 'next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import PlanDialog from './PlanDialog.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const useAlertMessage = useAlert;

const searchQuery = ref('');
const showDialog = ref(false);
const dialogError = ref(null);

const loading = computed(
  () => store.getters['pricingPlans/getUIFlags'].isFetching
);
const plans = computed(() => store.getters['pricingPlans/getRecords'] || []);

const filteredPlans = computed(() => {
  if (!searchQuery.value) return plans.value;
  const q = searchQuery.value.toLowerCase();
  return plans.value.filter(p => p.name.toLowerCase().includes(q));
});

const isIntegrationPlan = plan => isIntegrationPricingPlan(plan);
const isReadOnlyPlan = plan => isReadOnlyPricingPlan(plan);

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

onMounted(() => {
  store.dispatch('pricingPlans/get');
});

const openCreate = () => {
  dialogError.value = null;
  showDialog.value = true;
};

const openEdit = plan => {
  router.push({
    name: 'settings_pricing_plan_show',
    params: { planId: plan.id },
  });
};

const openPlan = plan => {
  router.push({
    name: 'settings_pricing_plan_show',
    params: { planId: plan.id },
  });
};

const handleSave = async data => {
  dialogError.value = null;
  try {
    await store.dispatch('pricingPlans/create', data);
    useAlertMessage(t('PRICING.PLANS.CREATE_SUCCESS'));
    showDialog.value = false;
  } catch (e) {
    dialogError.value =
      e?.response?.data?.message ||
      e?.response?.data?.errors?.name?.[0] ||
      t('PRICING.PLANS.SAVE_ERROR');
  }
};

const handleDelete = async plan => {
  try {
    await store.dispatch('pricingPlans/delete', plan.id);
    useAlertMessage(t('PRICING.PLANS.DELETE_SUCCESS'));
  } catch {
    useAlertMessage(t('PRICING.PLANS.DELETE_ERROR'));
  }
};

const handleToggleActive = async plan => {
  try {
    await store.dispatch('pricingPlans/update', {
      id: plan.id,
      active: !plan.active,
    });
  } catch {
    useAlertMessage(t('PRICING.PLANS.SAVE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('PRICING.LOADING')"
    :no-records-found="!loading && plans.length === 0"
    :no-records-message="t('PRICING.PLANS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('PRICING.PLANS.TITLE')"
        :description="t('PRICING.PLANS.DESCRIPTION')"
      >
        <template #actions>
          <input
            v-model="searchQuery"
            type="text"
            class="reset-base rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-10 w-52"
            :placeholder="t('PRICING.PLANS.SEARCH_PLACEHOLDER')"
          />
          <Button
            icon="i-lucide-plus"
            md
            :label="t('PRICING.PLANS.ADD_PLAN')"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.TABLE.PLAN_NAME') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.TABLE.VALID_FROM') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('PRICING.TABLE.VALID_TO') }}
            </th>
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11 w-24"
            >
              {{ t('PRICING.TABLE.ACTIVE') }}
            </th>
            <th
              class="py-4 ltr:text-right rtl:text-left font-semibold text-n-slate-11 w-32"
            >
              {{ t('PRICING.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="plan in filteredPlans"
            :key="plan.id"
            class="border-b border-n-weak cursor-pointer"
            @click="openPlan(plan)"
          >
            <td class="py-4 font-medium text-neutral-900 dark:text-white">
              <div class="flex items-center gap-2">
                {{ plan.name }}
                <span
                  v-if="plan.is_default"
                  class="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-woot-100 text-woot-700 dark:bg-woot-700/30 dark:text-woot-300"
                >
                  {{ t('PRICING.PLANS.DEFAULT_BADGE') }}
                </span>
                <span
                  v-if="isIntegrationPlan(plan)"
                  class="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-n-brand/10 text-n-brand"
                >
                  {{ t('PRICING.PLANS.INTEGRATION_BADGE') }}
                </span>
              </div>
            </td>
            <td class="py-4">
              {{ formatDate(plan.valid_from) }}
            </td>
            <td class="py-4">
              {{ formatDate(plan.valid_to) }}
            </td>
            <td class="py-4" @click.stop>
              <Switch
                v-if="!isReadOnlyPlan(plan)"
                :model-value="plan.active"
                @change="handleToggleActive(plan)"
              />
              <span v-else class="text-sm text-n-slate-10">
                {{ plan.active ? t('PRICING.TABLE.ACTIVE') : '—' }}
              </span>
            </td>
            <td class="py-4" @click.stop>
              <div class="flex items-center justify-end gap-2">
                <Button
                  v-if="!isReadOnlyPlan(plan)"
                  v-tooltip.top="t('PRICING.EDIT')"
                  icon="i-lucide-pen-line"
                  md
                  slate
                  faded
                  @click="openEdit(plan)"
                />
                <Button
                  v-if="!isIntegrationPlan(plan)"
                  v-tooltip.top="t('PRICING.DELETE')"
                  icon="i-lucide-trash-2"
                  md
                  ruby
                  faded
                  @click="handleDelete(plan)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <!-- Dialog -->
  <PlanDialog
    v-if="showDialog"
    :error="dialogError"
    @save="handleSave"
    @close="showDialog = false"
  />
</template>
